import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useCreateCategory, useUpdateCategory } from '../hooks/use-categories'
import { buildCategorySchema, categoryErrorKey } from '../types'
import {
  COLLECTOR_CODE,
  buildCategoryTree,
  parentBlockReason,
} from '../lib/build-tree'
import type { CategoryFormData } from '../types'
import type { Category } from '@/api/types'
import { ApiError } from '@/api/types'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

const NO_PARENT = 'none'

interface CategoryFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  categories?: Array<Category>
  /** Set when editing an existing group; null creates a new one. */
  editing?: Category | null
  /** Pre-selects a parent, for "add a subgroup in X". */
  presetParent?: Category | null
}

export function CategoryForm({
  open,
  onOpenChange,
  categories = [],
  editing = null,
  presetParent = null,
}: CategoryFormProps) {
  const { t: tCategories } = useTranslation('categories')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const canEdit = can(ACTIONS.CATEGORY_CREATE)
  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const [serverError, setServerError] = useState<string | null>(null)

  const isEdit = editing != null
  const isCollector = editing?.code === COLLECTOR_CODE

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(buildCategorySchema(tCategories)),
    defaultValues: {
      code: '',
      name: '',
      parentId: null,
      sortOrder: 0,
      active: true,
    },
  })

  useEffect(() => {
    if (!open) return
    setServerError(null)
    form.reset({
      code: editing?.code ?? '',
      name: editing?.name ?? '',
      parentId: editing?.parentId ?? presetParent?.id ?? null,
      sortOrder: editing?.sortOrder ?? 0,
      active: editing?.active ?? true,
    })
  }, [open, editing, presetParent, form])

  const showServerError = (error: unknown) => {
    const code = error instanceof ApiError ? error.code : undefined
    setServerError(tCategories(categoryErrorKey(code)))
  }

  const onSubmit = (data: CategoryFormData) => {
    if (!canEdit) return
    setServerError(null)

    if (editing) {
      updateCategory.mutate(
        {
          id: editing.id,
          data: {
            name: data.name,
            parentId: data.parentId,
            sortOrder: data.sortOrder,
            active: data.active,
          },
        },
        {
          onSuccess: () => onOpenChange(false),
          onError: showServerError,
        },
      )
      return
    }

    createCategory.mutate(data, {
      onSuccess: () => {
        form.reset()
        onOpenChange(false)
      },
      onError: showServerError,
    })
  }

  const pending = createCategory.isPending || updateCategory.isPending

  // Every category is offered, blocked ones included (4.15) — but in tree order, so a subgroup sits
  // under the group it belongs to instead of wherever its sortOrder drops it in the flat list.
  const parentOptions = useMemo(
    () => buildCategoryTree(categories),
    [categories],
  )

  // The category the parent options are judged against. When creating, nothing blocks except the
  // structural rules that do not depend on the row itself.
  const target: Category = editing ?? {
    id: -1,
    tenantId: -1,
    code: '',
    name: '',
    parentId: null,
    sortOrder: 0,
    active: true,
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? tCategories('form.editTitle') : tCategories('form.title')}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? tCategories('form.editDescription')
              : tCategories('form.description')}
          </DialogDescription>
        </DialogHeader>

        {serverError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tCategories('form.codeLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tCategories('form.codePlaceholder')}
                      {...field}
                      className="font-mono"
                      // The code is the identity the importer and every menu section resolve
                      // through, so it stays fixed once the category exists.
                      disabled={!canEdit || isEdit}
                    />
                  </FormControl>
                  <FormDescription>
                    {isEdit
                      ? tCategories('form.codeImmutable')
                      : tCategories('form.codeDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tCategories('form.nameLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tCategories('form.namePlaceholder')}
                      {...field}
                      disabled={!canEdit}
                    />
                  </FormControl>
                  <FormDescription>
                    {tCategories('form.nameDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="parentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tCategories('form.parentLabel')}</FormLabel>
                  <Select
                    value={
                      field.value == null ? NO_PARENT : String(field.value)
                    }
                    onValueChange={(value) =>
                      field.onChange(value === NO_PARENT ? null : Number(value))
                    }
                    disabled={!canEdit || isCollector}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_PARENT}>
                        {tCategories('form.parentNone')}
                      </SelectItem>
                      {parentOptions.map(({ category: candidate, depth }) => {
                        const reason = parentBlockReason(
                          candidate,
                          target,
                          categories,
                        )
                        return (
                          <SelectItem
                            key={candidate.id}
                            value={String(candidate.id)}
                            disabled={reason != null}
                          >
                            <span
                              className="flex w-full items-center justify-between gap-3"
                              style={{ paddingLeft: `${depth * 12}px` }}
                            >
                              <span>
                                {depth > 0 && (
                                  <span
                                    aria-hidden
                                    className="mr-1 text-muted-foreground"
                                  >
                                    ›
                                  </span>
                                )}
                                {candidate.name}
                              </span>
                              {reason && (
                                <span className="text-xs text-muted-foreground">
                                  {tCategories(`form.parentBlocked.${reason}`)}
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {isCollector
                      ? tCategories('form.parentCollectorLocked')
                      : tCategories('form.parentDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tCategories('form.sortOrderLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                      disabled={!canEdit}
                    />
                  </FormControl>
                  <FormDescription>
                    {tCategories('form.sortOrderDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canEdit}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tCategories('form.activeLabel')}</FormLabel>
                    <FormDescription>
                      {tCategories('form.activeDescription')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={pending || !canEdit}>
                {pending
                  ? tCommon('creating')
                  : isEdit
                    ? tCategories('form.save')
                    : tCategories('form.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
